import { UseFormRegister, FieldErrors, Control, Controller } from 'react-hook-form';

import CheckIcon from '~/shared/assets/icons/check.svg';
import RevertIcon from '~/shared/assets/icons/revert.svg';
import InfoCircleIcon from '~/shared/assets/icons/info-circle.svg';
import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';
import { Input } from '~/shared/components/Input';
import Switch from '~/shared/components/Switch';
import SimpleDropdown from '~/shared/components/SimpleDropdown';
import SizeInput from '~/settings/components/ConfigurationsPage/ConfigurationDetails/inputs/SizeInput';
import DurationInput from '~/settings/components/ConfigurationsPage/ConfigurationDetails/inputs/DurationInput';
import { parseConfigValue } from '~/settings/utils/configValueParser';
import { MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS } from '~/settings/components/ConfigurationsPage/helpers/utils';
import {
  parseNumericMinMax,
  parseSizeMinMax,
  createMinMaxValidate,
  parsePercentageValue,
  parsePositiveFloatValue,
  parseSizeValue,
} from '~/settings/components/ConfigurationsPage/ConfigurationDetails/helpers/minMaxValidation';

import type { ConfigField, ConfigFieldType } from '~/settings/components/ConfigurationsPage/types';

const INPUT_LAYOUT = {
  wrapperClassName: 'max-w-[160px]',
  inputClassName: 'px-1!',
} as const;

interface ConfigurationFieldProps {
  field: ConfigField;
  onChange: (fieldName: string, value: string | number) => void;
  onRevert: (fieldName: string) => void;
  register: UseFormRegister<Record<string, string | number>>;
  control: Control<Record<string, string | number>>;
  errors: FieldErrors<Record<string, string | number>>;
  isReverting?: boolean;
  isConfigReadOnlyUser?: boolean;
}

export default function ConfigurationField({
  field,
  onChange,
  onRevert,
  // register,
  control,
  errors,
  isReverting = false,
  isConfigReadOnlyUser = false,
}: ConfigurationFieldProps) {
  const displayDefault = field.defaultValue.toString();
  const fieldError = errors[field.name]?.message as string | undefined;

  const isBoolean =
    field.type === 'boolean' ||
    ['true', 'false'].includes(String(field.defaultValue).toLowerCase());

  const handleRevert = () => {
    onRevert(field.name);
  };

  const displayName =
    field.displayName ||
    field.name.replace(/[._]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const singularizeUnit = (unit: string, numericValue: number): string => {
    const unitMap: Record<string, { singular: string; plural: string }> = {
      s: { singular: 'second', plural: 'seconds' },
      m: { singular: 'minute', plural: 'minutes' },
      h: { singular: 'hour', plural: 'hours' },
    };

    const lowerUnit = unit.toLowerCase();
    const mapping = unitMap[lowerUnit];

    if (mapping) {
      return numericValue === 1 ? mapping.singular : mapping.plural;
    }

    return unit;
  };

  const formatDefaultForDisplay = (value: string, type: ConfigFieldType | undefined): string => {
    if (!type) {
      return value;
    }

    try {
      const parsed = parseConfigValue(value, type);
      if (parsed.unit) {
        const numericValue = parseFloat(parsed.displayValue);
        const singularizedUnit = singularizeUnit(parsed.unit, numericValue);
        return `${parsed.displayValue} ${singularizedUnit}`;
      }
      return value;
    } catch {
      return value;
    }
  };

  const getDefaultDisplay = () => {
    const valueToDisplay =
      field.valueOrigin === 'inherited'
        ? field.originalValue ?? field.currentValue
        : displayDefault;
    const formattedValue = field.type
      ? formatDefaultForDisplay(valueToDisplay, field.type)
      : valueToDisplay;

    if (field.valueOrigin === 'inherited' && field.inheritedFrom) {
      return `Inherited from ${field.inheritedFrom.layer}: ${formattedValue}`;
    }
    return formattedValue;
  };

  const renderInput = () => {
    if (isBoolean) {
      return (
        <Controller
          control={control}
          name={field.name}
          render={({ field: { value, onChange: fieldOnChange } }) => (
            <Switch
              size="md"
              checked={String(value).toLowerCase() === 'true'}
              disabled={isConfigReadOnlyUser}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.checked ? 'true' : 'false';
                fieldOnChange(newValue);
                onChange(field.name, newValue);
              }}
            />
          )}
        />
      );
    }

    switch (field.type) {
      case 'size': {
        const isDoubleDisplayField = (
          MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS as readonly string[]
        ).includes(field.name);
        const { minNum, maxNum } = parseSizeMinMax(field);
        const minBound = isDoubleDisplayField && minNum !== undefined ? minNum * 2 : minNum;
        const maxBound = isDoubleDisplayField && maxNum !== undefined ? maxNum * 2 : maxNum;
        const minMaxValidate = createMinMaxValidate(minBound, maxBound, parseSizeValue);
        const hasSizeValidation =
          isDoubleDisplayField || minNum !== undefined || maxNum !== undefined;
        const sizeRules = hasSizeValidation
          ? {
              validate: (value: string | number) => {
                const minMaxResult = minMaxValidate(value);
                if (minMaxResult !== true) {
                  return minMaxResult;
                }
                if (!isDoubleDisplayField) {
                  return true;
                }
                const str = String(value ?? '').trim();
                if (!str) {
                  return true;
                }
                const num = parseSizeValue(value);
                if (!Number.isFinite(num) || num !== Math.floor(num) || num % 2 !== 0) {
                  return 'Value must be an even whole number';
                }
                return true;
              },
            }
          : undefined;
        return (
          <Controller
            control={control}
            name={field.name}
            rules={sizeRules}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error } }) => (
              <SizeInput
                value={String(value)}
                onChange={(newValue) => {
                  fieldOnChange(newValue);
                  onChange(field.name, newValue);
                }}
                error={error?.message ?? fieldError}
                unit={field.unit}
                min={minBound}
                max={maxBound}
                disabled={isConfigReadOnlyUser}
              />
            )}
          />
        );
      }

      case 'duration':
        return (
          <Controller
            control={control}
            name={field.name}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error } }) => (
              <DurationInput
                value={String(value)}
                onChange={(newValue) => {
                  fieldOnChange(newValue);
                  onChange(field.name, newValue);
                }}
                error={error?.message ?? fieldError}
                unit={field.unit}
                disabled={isConfigReadOnlyUser}
              />
            )}
          />
        );

      case 'percentage': {
        const { minNum, maxNum } = parseNumericMinMax(field);
        const percentageRules = {
          validate: createMinMaxValidate(minNum, maxNum, parsePercentageValue),
        };
        return (
          <Controller
            control={control}
            name={field.name}
            rules={percentageRules}
            render={({
              field: { value, onChange: fieldOnChange, onBlur },
              fieldState: { error },
            }) => {
              const numericValue = String(value).replace('%', '').trim();
              const valueWithPercent = (v: string) =>
                v === '' ? '' : v.includes('%') ? v : `${v}%`;
              return (
                <Input
                  type="number"
                  value={numericValue}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const newValue = valueWithPercent(raw);
                    fieldOnChange(newValue);
                    onChange(field.name, newValue);
                  }}
                  onBlur={onBlur}
                  error={error?.message ?? fieldError}
                  wrapperClassName={INPUT_LAYOUT.wrapperClassName}
                  inputClassName={INPUT_LAYOUT.inputClassName}
                  step="any"
                  min={minNum}
                  max={maxNum}
                  disabled={isConfigReadOnlyUser}
                  rightAdornment={
                    <Typography variant="p3" className="text-gray-700">
                      %
                    </Typography>
                  }
                  rightAdornmentClassName="right-1!"
                />
              );
            }}
          />
        );
      }

      case 'positive_float': {
        const { minNum, maxNum } = parseNumericMinMax(field);
        const positiveFloatRules = {
          validate: createMinMaxValidate(minNum, maxNum, parsePositiveFloatValue),
        };
        return (
          <Controller
            control={control}
            name={field.name}
            rules={positiveFloatRules}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error } }) => (
              <Input
                type="number"
                value={String(value)}
                onChange={(e) => {
                  const newValue = e.target.value;
                  fieldOnChange(newValue);
                  onChange(field.name, newValue);
                }}
                error={error?.message ?? fieldError}
                wrapperClassName={INPUT_LAYOUT.wrapperClassName}
                inputClassName={INPUT_LAYOUT.inputClassName}
                step="any"
                min={minNum}
                max={maxNum}
                disabled={isConfigReadOnlyUser}
              />
            )}
          />
        );
      }

      case 'enum':
        return field.validValues && field.validValues.length > 0 ? (
          <Controller
            control={control}
            name={field.name}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error } }) => {
              const errMsg = error?.message ?? fieldError;
              return (
                <div className={INPUT_LAYOUT.wrapperClassName}>
                  <SimpleDropdown
                    items={field.validValues ?? []}
                    selectedItem={String(value)}
                    onSelect={(newValue) => {
                      fieldOnChange(newValue);
                      onChange(field.name, newValue);
                    }}
                    menuButtonClassName={INPUT_LAYOUT.inputClassName}
                    disabled={isConfigReadOnlyUser}
                  />
                  {errMsg && <span className="text-red text-xs mt-1 block">{errMsg}</span>}
                </div>
              );
            }}
          />
        ) : (
          <Controller
            control={control}
            name={field.name}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error } }) => (
              <Input
                type="text"
                value={String(value)}
                onChange={(e) => {
                  const newValue = e.target.value;
                  fieldOnChange(newValue);
                  onChange(field.name, newValue);
                }}
                wrapperClassName={INPUT_LAYOUT.wrapperClassName}
                inputClassName={INPUT_LAYOUT.inputClassName}
                error={error?.message ?? fieldError}
                disabled={isConfigReadOnlyUser}
              />
            )}
          />
        );

      default:
        return (
          <Controller
            control={control}
            name={field.name}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error } }) => (
              <Input
                type="text"
                value={String(value)}
                onChange={(e) => {
                  const newValue = e.target.value;
                  fieldOnChange(newValue);
                  onChange(field.name, newValue);
                }}
                wrapperClassName={INPUT_LAYOUT.wrapperClassName}
                inputClassName={INPUT_LAYOUT.inputClassName}
                error={error?.message ?? fieldError}
                disabled={isConfigReadOnlyUser}
              />
            )}
          />
        );
    }
  };

  return (
    <div className="flex flex-col py-3 border-b border-gray-200">
      <div className="flex justify-between items-start gap-2">
        <div className="flex gap2 w-full my-auto">
          <div className="flex flex-col px-[14px] min-w-96 py-3">
            <Typography variant="p3" className="font-semibold text-primary-900">
              {displayName}
            </Typography>
            <div className="mt-4 min-w-62">
              {field.description && (
                <Typography variant="buttons-2" color="text-gray-600" className="mt1">
                  {field.description}
                </Typography>
              )}
            </div>
          </div>
        </div>
        <div className="flex1 flex flex-col items-start gap2 w-full">
          <div className="flex-1 w-full my-auto">{renderInput()}</div>
          <div className="flex-1 py-1">
            <Typography variant="buttons-2" color="text-gray-700">
              {field.valueOrigin === 'inherited'
                ? getDefaultDisplay()
                : `Default: ${getDefaultDisplay()}`}
            </Typography>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1 min-w-26">
            {field.isOverridden ? (
              <>
                <InfoCircleIcon className="w-5 h-5 text-primary-300" />
                <Typography variant="p3" color="text-gray-600">
                  Overridden
                </Typography>
              </>
            ) : (
              <>
                <div className="w-4 h-4 grid place-items-center">
                  <CheckIcon className="w-3 h-3 text-gray-600" />
                </div>
                <Typography variant="p3" color="text-gray-600">
                  Default
                </Typography>
              </>
            )}
          </div>

          {!isConfigReadOnlyUser && (
            <div className="flex min-w-24 items-center">
              <Button
                leftIcon={
                  isReverting ? (
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <RevertIcon className="w-4 h-4" />
                  )
                }
                variant="text"
                size="sm"
                onClick={handleRevert}
                disabled={!field.isOverridden || isReverting}
                className="w-full group"
              >
                Revert
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 w-full py-1"></div>
    </div>
  );
}
